import React, { useState, useEffect } from 'react'

import update from 'react-addons-update'
import { useNavigate } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { APP_DEFAULT_STATE } from '@api/local'
import { CartPlugin } from '@api/local/CartPlugin'
import { ModalsPlugin, GlobalModalTypeEnum } from '@api/local/ModalsPlugin'
import { Heading, Paragraph, Pill, ResponsiveImage, Spacer, Button, IconEnum } from '@atoms/index'
import { useConfig } from '@client/contexts/ConfigProvider'
import { Ellipsis, ProductCardContainer, ProductCardImageContainer, ResponsivePXValue, theme } from '@components/Theme'
import { useEvents } from '@contexts/GTMProvider'
import {
  MealKitDishListFragment, UserMenuFragment, useAddDishToUserMenuMutation, useAddItemToCartMutation, useRemoveDishFromUserMenuMutation,
  useUserCartQuery, useUserDetailsQuery, useGetAppQuery,
  MealKitDishDetailsFragment,
} from '@hooks/api'
import { ItemInterfaceNew } from '@lib/GTM'
import { Utilities } from '@lib/Utilities'
import { getCategoryBackground, DiscoveryVitality, SelectOption, Form, SelectInput, convertEnumToNum, useForm, FieldData, HashAnchor } from '@molecules/index'
import { MealkitModal, MealkitModalEnum, ReactivateSubscriptionModal } from '@organisms/index'
import { NumberOfPortionsEnum, ProductAvailabilityEnum } from '@uctypes/api/globalTypes'

interface CategoryContainerProps {
  $backgroundColor?: string
}

const Container = styled.div`
  border: 1px solid black;
  border-radius: 6px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  background: ${(props): string => props.theme.colors.whites.pureWhite};
  display: flex;
  flex-direction: column; /* was row */
  align-items: center; /* center items including image */
  ${ResponsivePXValue('max-width', { mobile: '104px', desktop: '150px' })}
`
const ImageContainer = styled.div`
  cursor: pointer;
  align-items: center;
  justify-content: center;
  ${ResponsivePXValue('width', { mobile: '86px', desktop: '134px' })}
  ${ResponsivePXValue('margin', { mobile: '8px 4px', tablet: '8px 4px', desktop: '8px 4px' })}

  img {
    width: 100%;
    aspect-ratio: 3 / 1.4;
    object-fit: cover;
    border-radius: 4px;
  }
`

const CategoryContainer = styled.div<CategoryContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  border-radius: 4px;
  background-color: ${(props) => props.$backgroundColor || 'transparent'};
  ${ResponsivePXValue('max-width', { mobile: '90px', desktop: '126px' })}

  ${ResponsivePXValue('padding', { mobile: '2px 8px', tablet: '2px 8px', desktop: '5px 8px' })}
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: fit-content;  
  flex: 1;

  background-color: ${(props): string => props.theme.colors.whites.pureWhite};
  ${ResponsivePXValue('margin-bottom', { mobile: '8px', tablet: '8px', desktop: '8px' })}
  ${ResponsivePXValue('max-width', { mobile: '90px', desktop: '126px' })}
  ${ResponsivePXValue('height', { mobile: '80px', tablet: '80px', desktop: '140px' })}
  .title {
    margin: 0;
    line-height: 125%;

  }
 

  .no-margin {    
    align-items: center;
    height: fit-content;
  }

  .left-icon {
    bottom: 0;  
  }

  .select-input {
    ${ResponsivePXValue('padding-left', '28px')}
  }

  .select-option-container {
    ${ResponsivePXValue('transform', 'translateY(-200px)')} 
  }

  `
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const Overlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: ${(props): string => props.theme.colors.misc.overlay};
`

const LinkContainer = styled.a`
  cursor: pointer;
  text-decoration: none;
`

enum DisplayTypeEnum {
  STANDARD = 'STANDARD',
  PARING = 'PARING',
}

export interface MealKitModalCardProps {
  mealKit: MealKitDishDetailsFragment
  className?: string
  loading?: boolean
  isMobile?: boolean
}

interface MealKitModalCardState {
  displayType: DisplayTypeEnum
  chosenPairing: number
  wineQuantities: number[]
  addToCartInterrupted: boolean
  activeModal: MealkitModalEnum
  displayReactivationModal: boolean
  loading: boolean
  portionSize: NumberOfPortionsEnum
}

const DEFAULT_STATE: MealKitModalCardState = {
  displayType: DisplayTypeEnum.STANDARD,
  chosenPairing: 0,
  wineQuantities: [],
  addToCartInterrupted: false,
  activeModal: MealkitModalEnum?.NONE,
  displayReactivationModal: false,
  loading: false,
  portionSize: NumberOfPortionsEnum.SERVES_2,
}

export const MealKitModalCard = React.memo(({ mealKit, className, isMobile }: MealKitModalCardProps): JSX.Element => {

  const [state, setState] = useState<MealKitModalCardState>(DEFAULT_STATE)
  const [form] = useForm()
  const config = useConfig()
  const navigate = useNavigate()
  const { addToast } = useToasts()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const { data: userCartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [addItemToCart] = useAddItemToCartMutation()
  const [addDishToBox] = useAddDishToUserMenuMutation()
  const [removeItemFromCart] = useRemoveDishFromUserMenuMutation()
  const events = useEvents()
  const hasSelectedPlan = userCartData?.currentUser?.checkoutStatus?.hasSelectedPlan
  const category = mealKit?.mealKitCategories[0]
  const hasLunchCategory = !category?.canSubscribe
  const categoryTitle = category.title
  const background = getCategoryBackground(category.background)
  const buttonText = hasSelectedPlan ? (mealKit.quantityInCart > 0 ? 'ADD ANOTHER' : 'ADD TO ORDER') : 'GET STARTED'
  const buttonColor = mealKit.quantityInCart > 0 ? 'green' : 'black'
  const isDisplayingPairing = state.displayType === DisplayTypeEnum.PARING
  const checkoutStatus = userCartData?.currentUser?.checkoutStatus
  const servingSizeNumber = convertEnumToNum(state.portionSize)

  const defaultPortions = appData.app.defaultPortions ? appData.app.defaultPortions: (NumberOfPortionsEnum.SERVES_2)

  useEffect(() => {
    const list: number[] = []
    for (let i = 0; i < mealKit?.activeParings?.length; i++) {
      list.push(1)
    }
    if (mealKit?.activeParings) {
      setState((prevState) => update(prevState, {
        wineQuantities: { $set: list },
      }))
    }
  }, [mealKit?.activeParings])

  const _handleAddDishToBox = async (): Promise<void> => {
    _setLoading(true)

    const portionSize = state.portionSize

    try {
      await addDishToBox({
        variables: {
          input: {
            dishId: mealKit?.id,
            portionSize,
          },
        },
      })

      const logData = {
        itemName: mealKit?.name,
        itemId: mealKit?.products.find(product => product.portionSize as unknown as NumberOfPortionsEnum === portionSize)?.id,
        itemGroupId: mealKit?.id,
        price: mealKit?.products.find(product => product.portionSize as unknown as NumberOfPortionsEnum === portionSize)?.price,
        itemBrand: 'UCOOK',
        itemCategory: mealKit?.mealKitCategories?.map((cat) => cat.title)?.join(', '),
        itemVariant: mealKit?.mealKitCategories?.map((cat) => cat.title)?.join(', '),
        itemListName: 'Meal Kit',
        quantity: 1,
        itemImage: mealKit?.coverImage?.location,
        itemStockCount: 1,
        itemChef: mealKit?.chef?.name,
        isMealkit: 'yes',
        itemServingSize: servingSizeNumber,
      }
      const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew

      events.hasAddedToCart(snakedData)

      addToast('Item successfully added to your order. Hit \'save\' in your cart before you go, or this dish won’t be saved.', {
        appearance: 'warning',
        autoDismiss: true,
      })
    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
    _setLoading(false)
  }

  const _setLoading = (loading: boolean) => {
    setState((prevState) => update(prevState, {
      loading: { $set: loading },
    }))
  }

  const pdpLink = `/meal-kit/${mealKit?.slug}`
  const _handleLinkClicked = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    navigate(pdpLink)
  }

  return (
    <Container className={className}>

      <If condition={mealKit.discoveryHealthyDiningItem}>
        <DiscoveryVitality />
      </If>
      <LinkContainer href={pdpLink} onClick={_handleLinkClicked}>
        <ImageContainer>
          <ResponsiveImage image={mealKit.coverImage} />
        </ImageContainer>
      </LinkContainer>
      <ContentContainer>
        <LinkContainer href={pdpLink} onClick={_handleLinkClicked}>
          <TextContainer>
            <Paragraph className='title' variant='p3' bold>
                {mealKit.name.replace(/\s*\(serves\s*\d+\)/i, '')}
            </Paragraph>
          </TextContainer>
        </LinkContainer>
        <Spacer mobile='4px' desktop='8px' />
        <CategoryContainer $backgroundColor={background}>
          <Paragraph variant='p3'>{categoryTitle}</Paragraph>
        </CategoryContainer>
      </ContentContainer>

    </Container>
  )
})

